import { Component, effect, inject, input, linkedSignal, model } from '@angular/core';
import { provideIcons } from '@ng-icons/core';
import { lucideEye } from '@ng-icons/lucide';
import { HlmAutocompleteImports, provideHlmAutocompleteConfig } from '@spartan-ng/helm/autocomplete';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';

import { FormBuilder, FormsModule } from '@angular/forms';
import { Module } from '../../../référentiel-métier-parcours/models/module.model';
import { Interrelation } from '../../../référentiel-métier/référentiel-metier-interrelations/models/interrelation.model';
import { InterrelationStore } from '../../../référentiel-métier/référentiel-metier-interrelations/store/interrelation.store';
import { Aptitude } from '../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { Production } from '../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/production.model';
import { SéquenceFormService } from '../séquence-form.service';
import { SéquenceObjectifGroupComponent } from './objectif-group/objectif-group.component';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-detail-objectifs',
  imports: [
    HlmCheckboxImports,
    HlmLabelImports,
    FormsModule,
    HlmAutocompleteImports,
    HlmButtonImports,
    HlmIconImports,
    HlmTooltipImports,
    SéquenceObjectifGroupComponent,
  ],
  templateUrl: './detail-objectifs.component.html',
  providers: [
    provideHlmAutocompleteConfig({
      transformOptionToString: (option: Aptitude) => option.nom,
      transformValueToSearch: (option: Aptitude) => option.nom,
    }),
    provideIcons({ lucideEye }),
  ],
})
export class DetailObjectifsComponent {
  readonly #fb = inject(FormBuilder);
  readonly detailFormService = inject(SéquenceFormService);
  readonly interrelationStore = inject(InterrelationStore);

  readonly referentiel = input.required<Référentiel>();
  readonly module = input.required<Module>();
  readonly aptitudesDisponibles = linkedSignal(() => this.detailFormService.aptitudesDisponibles());

  readonly aptitudesFiltrees = linkedSignal(() => this.aptitudesDisponibles());

  public readonly nouvelleAptitude = model<Aptitude | null>();

  constructor() {
    effect(() => {
      const nouvelleAptitude = this.nouvelleAptitude();
      if (nouvelleAptitude) {
        const objectifForm = this.#fb.group({
          aptitude: [nouvelleAptitude],
          interrelations: [[...this.getInterrelationsFromAptitude(nouvelleAptitude)]],
          productions: [[...this.getProductionsFromAptitude(nouvelleAptitude)]],
          estEvaluee: [false],
          objectif: [],
        });
        this.objectifs.push(objectifForm);
        this.nouvelleAptitude.set(null);
      }
    });
  }
  filterAptitudes(search: string) {
    const aptitudesDisponibles = this.aptitudesDisponibles();
    if (aptitudesDisponibles) {
      if (!search) this.aptitudesFiltrees.set(aptitudesDisponibles);

      this.aptitudesFiltrees.set(
        aptitudesDisponibles.filter((x) => x.nom.toLowerCase().includes(search.toLowerCase())),
      );
    }
  }

  getInterrelationsFromAptitude(aptitude: Aptitude) {
    const module = this.module();
    let interrelations: Interrelation[] = [];

    if (module) {
      interrelations = this.interrelationStore
        .entities()
        .filter(
          (x) =>
            x.idsPhase?.includes(aptitude.idPhase) &&
            module.interrelations.some((y) => y.idInterrelation === x.id && y.idAptitude === aptitude.id),
        );
    }
    return interrelations;
  }

  getProductionsFromAptitude(aptitude: Aptitude) {
    const module = this.module();
    let productions: Production[] = aptitude.productions ?? [];
    if (module) {
      productions = aptitude.productions?.filter((x) => module.productions.some((y) => y.id === x.id)) ?? [];
    }
    return productions;
  }
  redirectToRessourcesPro() {
    const url = location.href;
    const fragments = url.split('/').slice(0, 7);
    fragments.push('ressources-professionnelles');
    window.open(fragments.join('/'), '_blank');
  }

  supprimerAptitude(index: number) {
    this.objectifs.removeAt(index);
  }

  get objectifs() {
    return this.detailFormService.objectifs;
  }
}
